import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const BOOKING_URL = 'https://api.leadconnectorhq.com/widget/bookings/eligibility-next-steps-call';

const IntakeComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Get answers from navigation state (passed from /intake questionnaire)
  const answers = location.state?.answers || {};

  // Validate US phone format
  const validatePhone = (phoneValue) => {
    const cleaned = phoneValue.replace(/\D/g, '');
    return cleaned.length === 10 || cleaned.length === 11;
  };

  // Format phone as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    if (fieldErrors.phone) {
      setFieldErrors(prev => ({ ...prev, phone: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!firstName.trim()) {
      errors.firstName = 'Please complete this field';
    }
    
    if (!email.trim()) {
      errors.email = 'Please complete this field';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!phone.trim()) {
      errors.phone = 'Please complete this field';
    } else if (!validatePhone(phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);

    try {
      const cleanedPhone = phone.replace(/\D/g, '');
      const consentTimestamp = new Date().toISOString();
      const sourceUrl = window.location.href;
      
      const response = await fetch(`${API}/api/assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName.trim(),
          phone: cleanedPhone,
          sms_consent: smsConsent,
          marketing_consent: marketingConsent,
          consent_timestamp: consentTimestamp,
          source_url: sourceUrl,
          answers,
          completedAt: consentTimestamp,
          source: 'intake_assessment'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit assessment');
      }

      // Send webhook to Lead Connector
      try {
        await fetch('https://services.leadconnectorhq.com/hooks/ciW4hzzEC7Eyt0s6RHTl/webhook-trigger/d5f2eecc-3b9f-4fdc-928b-6883bcacc0d3', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: firstName.trim(),
            last_name: '',
            email: email,
            phone: cleanedPhone
          })
        });
      } catch (webhookErr) {
        // Log webhook error but don't block form submission
        console.error('Webhook error:', webhookErr);
      }

      // Fire tracking events
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Hormone Assessment Complete',
          content_category: 'Assessment',
          first_name: firstName.trim(),
          email: email
        });
      }

      // Set session storage flag
      sessionStorage.setItem('glow_assessment_completed', 'true');

      // Show success state
      setSubmitted(true);

    } catch (err) {
      console.error('Submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success/Thank You view
  if (submitted) {
    return (
      <div className="page-intake-complete">
        <div className="intake-complete-container">
          <div className="intake-complete-card">
            <div className="intake-complete-success">
              <div className="success-icon">
                <Check size={48} />
              </div>
              <h1 className="intake-complete-title">Your Results Show Signs of Hormone Imbalance</h1>
              
              <p className="intake-complete-message">
                Based on your responses, you may be a strong candidate for personalized hormone optimization.
              </p>
              
              <p className="intake-complete-message">
                Your symptom pattern aligns with clinical indicators often associated with fatigue, 
                weight resistance, mood shifts, and metabolic disruption.
              </p>
              
              <p className="intake-complete-message">
                The next step is a brief 1:1 eligibility consultation with a licensed provider to 
                review your results and determine if treatment is appropriate for you.
              </p>
              
              <p className="intake-complete-urgency">
                Spots are limited each week to ensure individualized care.
              </p>
              
              <a 
                href="https://start.myglowhealth.co/schedule" 
                className="btn-schedule-call"
                data-testid="schedule-call-btn"
              >
                Start My Free Eligibility Call
                <ArrowRight size={18} />
              </a>

              <div className="intake-complete-reassurance">
                <span className="reassurance-item">
                  <Check size={14} />
                  Reviewed by a licensed clinician
                </span>
                <span className="reassurance-item">
                  <Check size={14} />
                  No obligation
                </span>
                <span className="reassurance-item">
                  <Check size={14} />
                  Private & HIPAA-secure
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Contact capture form view
  return (
    <div className="page-intake-complete">
      <div className="intake-complete-container-single">
        <div className="intake-complete-card-single">
          {/* Header */}
          <div className="intake-header-single">
            <div className="intake-complete-check">
              <Check size={32} />
            </div>
            <h1 className="intake-complete-title">Your results are ready.</h1>
            <p className="intake-subtext-single">
              Based on your responses, you may benefit from a provider consultation. 
              Book your free 15-minute eligibility call now, <strong>spots are limited this week.</strong>
            </p>
          </div>

          {/* Contact Form Section */}
          <div className="intake-form-section-single">
            <div className="intake-form-field">
              <label className="intake-label">First Name <span className="required">*</span></label>
              <input
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (fieldErrors.firstName) setFieldErrors(prev => ({ ...prev, firstName: null }));
                }}
                className={`intake-input-single ${fieldErrors.firstName ? 'input-error' : ''}`}
                data-testid="intake-firstname-input"
              />
              {fieldErrors.firstName && <span className="field-error-message">{fieldErrors.firstName}</span>}
            </div>

            <div className="intake-form-field">
              <label className="intake-label">Email Address <span className="required">*</span></label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                }}
                className={`intake-input-single ${fieldErrors.email ? 'input-error' : ''}`}
                data-testid="intake-email-input"
              />
              {fieldErrors.email && <span className="field-error-message">{fieldErrors.email}</span>}
            </div>

            <div className="intake-form-field">
              <label className="intake-label">Mobile Phone <span className="required">*</span></label>
              <input
                type="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={handlePhoneChange}
                className={`intake-input-single ${fieldErrors.phone ? 'input-error' : ''}`}
                data-testid="intake-phone-input"
              />
              {fieldErrors.phone && <span className="field-error-message">{fieldErrors.phone}</span>}
            </div>

            {/* SMS Consent Checkbox */}
            <div className="sms-consent-field-single">
              <input
                type="checkbox"
                id="sms-consent"
                name="sms_consent"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="sms-consent-checkbox"
                data-testid="sms-consent-checkbox"
              />
              <label htmlFor="sms-consent" className="sms-consent-label-single">
                I agree to receive SMS messages from Glow Health LLC regarding scheduling, appointment reminders, and care coordination. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out. Reply HELP for assistance. View our <a href="https://myglowhealth.co/privacy-policy" className="consent-link">Privacy Policy</a> and <a href="https://myglowhealth.co/terms-of-service" className="consent-link">Terms of Service</a>.
              </label>
            </div>
            <p className="consent-disclaimer-single">Consent is not a condition of purchase.</p>
          </div>

          {/* Calendar Section */}
          <div className="intake-calendar-section-single">
            <iframe
              src={BOOKING_URL}
              title="Book Eligibility Call"
              className="booking-iframe-single"
              frameBorder="0"
            />
          </div>

          {error && <p className="intake-error">{error}</p>}

          {/* CTA Button */}
          <button 
            type="button"
            onClick={handleSubmit}
            className="btn-book-call-single"
            disabled={isSubmitting}
            data-testid="intake-submit-btn"
          >
            {isSubmitting ? 'Booking...' : 'Book My Call'}
          </button>

          {/* Trust Badges */}
          <div className="trust-badges-single">
            <span>Private &amp; confidential</span>
            <span className="trust-divider">·</span>
            <span>Reviewed by a clinician</span>
            <span className="trust-divider">·</span>
            <span>No spam</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeComplete;
